import { IonText } from '@ionic/react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router';

import BackButton from '@components/buttons/BackButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import InitialConsultBasicInfoForm from '@components/forms/InitialConsultBasicInfoForm';
import InitialConsultClientInfoForm from '@components/forms/InitialConsultClientInfoForm';
import InitialConsultConclusionForm from '@components/forms/InitialConsultConclusionForm';
import InitialConsultFinancialForm from '@components/forms/InitialConsultFinancialForm';
import InitialConsultFunctionalForm from '@components/forms/InitialConsultFunctionalForm';
import InitialConsultLegalForm from '@components/forms/InitialConsultLegalForm';
import InitialConsultMedicalForm from '@components/forms/InitialConsultMedicalForm';
import InitialConsultSocialForm from '@components/forms/InitialConsultSocialForm';
import InitialConsultWellbeingForm from '@components/forms/InitialConsultWellbeingForm';
import InternalNoteForm from '@components/forms/InternalNoteForm';
import StageForm from '@components/forms/StageForm';
import InitialConsultMenu from '@components/InitialConsultMenu';
import styles from '@components/InitialConsultReport.module.css';
import InternalNote from '@components/InternalNote';
import Modal from '@components/Modal';
import DateGroupBanner from '@components/text/DateGroupBanner';
import HugeText from '@components/text/HugeText';
import useDestroyInternalNote from '@hooks/mutations/useDestroyInternalNote';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  ConsultFragment as Consult,
  FocusAreaEnum,
  GetJourneyWithInitialConsultQuery,
  InternalNoteFragment as InternalNoteType
} from '@typing/Generated';
import { InitialConsultReportSection } from '@typing/Models';
import { groupByTimestampDay, initialConsultCurrentSection } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  consult: Consult | undefined;
  journey: GetJourneyWithInitialConsultQuery['journey'];
};

const InitialConsultReportEdit = ({ consult, journey }: Props) => {
  const myExpert = useMyExpert();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();
  const currentSection = useMemo(() => initialConsultCurrentSection(location.pathname), [location]);

  const internalNoteDeleteOpener = useOpener();
  const [internalNoteBeingDeleted, setInternalNoteBeingDeleted] = useState<InternalNoteType | undefined>(undefined);

  const internalNoteModalOpener = useOpener();
  const [internalNoteBeingEdited, setInternalNoteBeingEdited] = useState<InternalNoteType | undefined>(undefined);

  const destroyInternalNote = useDestroyInternalNote({ journeyId: journey.id });

  const handleInternalNoteDelete = useCallback(() => {
    if (internalNoteBeingDeleted) {
      destroyInternalNote({ id: internalNoteBeingDeleted.id });
      setInternalNoteBeingDeleted(undefined);
      internalNoteDeleteOpener.close();
    }
  }, [destroyInternalNote, internalNoteBeingDeleted, internalNoteDeleteOpener]);

  const handleStartInternalNoteEdit = useCallback(
    (note: InternalNoteType) => {
      setInternalNoteBeingEdited(note);
      internalNoteModalOpener.open();
    },
    [internalNoteModalOpener]
  );

  const handleStartInternalNoteDelete = useCallback(
    (note: InternalNoteType) => {
      setInternalNoteBeingDeleted(note);
      internalNoteDeleteOpener.open();
    },
    [internalNoteDeleteOpener]
  );

  const internalNoteDayGroups = useMemo(() => {
    if (journey.initialConsultReport?.internalNotes) {
      return groupByTimestampDay<InternalNoteType>(
        journey.initialConsultReport.internalNotes,
        internalNote => internalNote.createdAt,
        intl
      );
    }
    return [];
  }, [intl, journey.initialConsultReport.internalNotes]);

  const handleNextPage = useCallback(
    (nextPage?: string) => {
      if (nextPage) {
        history.push(
          journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: `/initial-consult-report/edit/${nextPage}${consult?.id ? `/${consult.id}` : ''}`,
            myExpert
          })
        );
      } else {
        history.push(
          journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: '/initial-consult-report',
            myExpert
          })
        );
      }
    },
    [consult?.id, history, journey.id, myExpert]
  );

  return (
    <>
      <div className={styles.backButtonSpacing}>
        <BackButton
          routerLink={journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: '/care-profile',
            myExpert
          })}
        />
      </div>
      <div className="ion-hide-sm-up ion-margin-vertical">
        <TertiaryButton
          icon="chevronLeft"
          routerLink={journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: `/initial-consult-report/edit${consult?.id ? `/${consult.id}` : ''}`,
            myExpert
          })}
          textKey="dictionary.menu"
        />
      </div>
      <div className={styles.splitPage} data-test="editing-report">
        <div
          className={
            currentSection === InitialConsultReportSection.MAIN ? styles.menu : 'ion-hide-sm-down ' + styles.menu
          }
        >
          <InitialConsultMenu consultId={consult?.id} currentSection={currentSection} editing journeyId={journey.id} />
        </div>
        <div className={styles.content}>
          <div
            className={`${styles.internalNotes}  ${
              currentSection === InitialConsultReportSection.MAIN ? 'ion-hide-sm-down' : ''
            }`}
            data-test="internal-notes"
          >
            <>
              <InternalNoteForm
                initialConsultReportId={journey.initialConsultReport.id}
                journeyId={journey.id}
                skipCancel
              />
              {internalNoteDayGroups.map(group => (
                <div className={styles.internalNoteSpacing} key={`${group.year}-${group.month}-${group.date}`}>
                  <div className={styles.internalNoteSpacing}>
                    <DateGroupBanner date={group.date} month={group.month} year={group.year} />
                  </div>
                  {group.models.map(internalNote => (
                    <InternalNote
                      consult={consult}
                      journeyId={journey.id}
                      key={internalNote.id}
                      note={internalNote}
                      onDelete={handleStartInternalNoteDelete}
                      onEdit={handleStartInternalNoteEdit}
                    />
                  ))}
                </div>
              ))}

              <DeleteConfirmationAlert
                messageKey="models.internalNote.reallyDelete"
                onDelete={handleInternalNoteDelete}
                opener={internalNoteDeleteOpener}
              />

              <Modal
                opener={internalNoteModalOpener}
                title={intl.formatMessage({ id: 'models.internalNote.editInternalNote' })}
              >
                {() => (
                  <InternalNoteForm
                    consult={consult}
                    internalNote={internalNoteBeingEdited}
                    journeyId={journey.id}
                    onCancel={internalNoteModalOpener.close}
                    onSuccess={internalNoteModalOpener.close}
                  />
                )}
              </Modal>
            </>
          </div>
          <div
            className={`${styles.questions} ${
              currentSection === InitialConsultReportSection.MAIN ? 'ion-hide-sm-down' : ''
            }`}
          >
            <HugeText>
              <FormattedMessage id={`pages.client.careProfile.sections.${currentSection}`} />
            </HugeText>
            <div
              className={
                currentSection === InitialConsultReportSection.CLIENT_INFO ||
                currentSection === InitialConsultReportSection.MAIN
                  ? undefined
                  : 'ion-hide'
              }
            >
              <div className="ion-margin-top">
                <IonText>
                  <FormattedMessage id="models.initialConsultReport.questions.client_name.content" />
                </IonText>
                <div className={styles.uneditableItem}>
                  <IonText>{journey.owner.fullName}</IonText>
                </div>
              </div>
              <div className="ion-margin-top">
                <IonText>
                  <FormattedMessage id="models.initialConsultReport.questions.employer.content" />
                </IonText>
                <div className={styles.uneditableItem}>
                  <IonText>{journey.owner.employer?.name}</IonText>
                </div>
              </div>
              <InitialConsultClientInfoForm
                careProfile={journey.careProfile}
                client={journey.initialConsultReport.journey.owner}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === InitialConsultReportSection.BASIC_INFO ? undefined : 'ion-hide'}>
              <InitialConsultBasicInfoForm
                careProfile={journey.careProfile}
                client={journey.owner}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                journeyId={journey.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === FocusAreaEnum.medical ? undefined : 'ion-hide'}>
              <InitialConsultMedicalForm
                careProfile={journey.careProfile}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === FocusAreaEnum.functional ? undefined : 'ion-hide'}>
              <InitialConsultFunctionalForm
                careProfile={journey.careProfile}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === FocusAreaEnum.legal ? undefined : 'ion-hide'}>
              <InitialConsultLegalForm
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                legalDocuments={journey.legalDocuments}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === FocusAreaEnum.financial ? undefined : 'ion-hide'}>
              <InitialConsultFinancialForm
                careProfile={journey.careProfile}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === FocusAreaEnum.social ? undefined : 'ion-hide'}>
              <InitialConsultSocialForm
                careProfile={journey.careProfile}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === FocusAreaEnum.wellbeing ? undefined : 'ion-hide'}>
              <InitialConsultWellbeingForm
                client={journey.owner}
                consultId={consult?.id}
                initialConsultReportId={journey.initialConsultReport.id}
                journeyId={journey.id}
                onSuccess={handleNextPage}
              />
            </div>
            <div className={currentSection === InitialConsultReportSection.STAGE ? undefined : 'ion-hide'}>
              <StageForm inInitialConsultReport journey={journey} onSuccess={handleNextPage} />
            </div>
            <div className={currentSection === InitialConsultReportSection.CONCLUSION ? undefined : 'ion-hide'}>
              <InitialConsultConclusionForm
                careProfile={journey.careProfile}
                clientRoles={journey.clientRoles}
                consult={consult}
                initialConsultReport={journey.initialConsultReport}
                journeyId={journey.id}
                onSuccess={handleNextPage}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InitialConsultReportEdit;
